import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import Layout from '../../components/layout/Layout';
import 'bootstrap/dist/css/bootstrap.min.css';
import Modal from 'react-bootstrap/Modal';
import {
    Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography,
    Button, TextField, Select, MenuItem, Pagination, InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Progress from '../../hooks/useProgressBar';
import useDebounce from '../../hooks/useDebounce';
import { getData, getPresignedUrl, uploadToS3, postData, putData } from '../../services/apiService';
import useFetchIp from "../../hooks/useFetchIp";

const SupportTicket = () => {
    const { ip } = useFetchIp();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [addSupportTicketModal, setAddSupportTicketModal] = useState(false);
    const [formData, setFormData] = useState({ title: "", message: "", page_url: "", ticket_image: "" });
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearchQuery = useDebounce(searchQuery, 500);
    const [totalPages, setTotalPages] = useState(1);

    // Fetch on load
    useEffect(() => {
        fetchData();
    }, [page, rowsPerPage, debouncedSearchQuery]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await getData(`/get-ticket?page=${page}&limit=${rowsPerPage}&search=${debouncedSearchQuery}`);
            if (response) {
                setData(response.data);
                setTotalPages(response.totalPages);
            }
            setLoading(false);
        } catch (error) {
            console.error('Failed to fetch data', error);
            setLoading(false);
        }
    }

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setPage(1);
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(event.target.value);
        setPage(1);
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const { uploadUrl, fileKey, publicUrl } = await getPresignedUrl(file.type);

            await uploadToS3(uploadUrl, file);

            setFormData(prev => ({
                ...prev,
                ticket_image: publicUrl,
                fileKey
            }));
        } catch (error) {
            console.error('Error uploading file:', error);
        }
    };

    const addSupportTicket = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const dataToSend = {
                ...formData,
                ipAddress: ip
            };
            const response = await postData('/create-ticket', dataToSend);
            setFormData({ title: "", message: "", page_url: "", ticket_image: "" });
            setAddSupportTicketModal(false);
            fetchData();
            setLoading(false);
        } catch (error) {
            setLoading(false);
        }
    };

    const handleCloseTicket = async (id) => {
        try {
            await putData(`/close-ticket/${id}`, { ipAddress: ip });
            fetchData();
        } catch (error) {
            console.error('Failed to close ticket', error);
        }
    };

    return (
        <Layout>
            {loading ? <Progress /> :
                <Box className="p-6 bg-gray-100 min-h-screen">
                    <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold', mb: 3 }}>Support Tickets</Typography>
                    {/* Header: Add Button (left) and Search (right) */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2, }} >
                        <Button variant="contained" sx={{
                            borderRadius: 2, fontWeight: 600, boxShadow: 2, px: 3, py: 1, textTransform: 'none', backgroundColor: '#4B49AC',
                            '&:hover': { backgroundColor: '#3A3897', },
                        }} onClick={() => { setAddSupportTicketModal(true); }}> + Add Support Ticket </Button>
                        <TextField placeholder="Search Lession" value={searchQuery} onChange={handleSearchChange} variant="outlined" size="medium"
                            sx={{ width: 350, bgcolor: '#e9ecef', borderRadius: 2, '& .MuiOutlinedInput-root': { borderRadius: 2, }, }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: '#757575' }} />
                                    </InputAdornment>
                                ),
                                sx: { bgcolor: '#e9ecef', borderRadius: 2 },
                            }}
                        />
                    </Box>
                    {/* <TableContainer component={Paper} elevation={0} sx={{ mb: 2, borderRadius: 2, boxShadow: 2, overflow: 'hidden', }} > */}

                    <TableContainer component={Paper}
                        sx={{
                            width: "100%",
                            maxWidth: "100%",
                            overflowX: "auto",
                            overflowY: "hidden",
                            "&::-webkit-scrollbar": {
                                height: "8px"
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#888",
                                borderRadius: "4px"
                            }
                        }}
                    >
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#4B49AC' }}>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopLeftRadius: 8 }}> ID </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Ticket No </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Topic </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Description </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Page url </TableCell>
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Status </TableCell>
                                    {/* <TableCell align="center" sx={{ color: 'white', fontWeight: 600 }}> Ticket Image </TableCell> */}
                                    <TableCell align="center" sx={{ color: 'white', fontWeight: 600, borderTopRightRadius: 8 }}> Action </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.map((item, index) => (
                                    <TableRow key={item._id}>
                                        <TableCell align="center">
                                            <Typography variant="body1">{(page - 1) * rowsPerPage + index + 1}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.support_ticket_no}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.title}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.message}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1">{item.page_url}</Typography>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography variant="body1"
                                                sx={{
                                                    color: item.status === "OPEN" ? "orange" : item.status === "IN-REVIEW"
                                                        ? "red" : item.status === "RESOLVED" ? "green" : "inherit", fontWeight: "bold",
                                                }}
                                            > {item.status}
                                            </Typography>
                                        </TableCell>

                                        {/* <TableCell align="center">
                                            <Typography variant="body1"><img src={item.ticket_image} alt="" className="img-fluid" style={{ width: "60px", height: "70px" }} /></Typography>
                                        </TableCell> */}
                                        <TableCell align="center">
                                            <Button variant="contained" color="success" size="small" onClick={() => handleCloseTicket(item._id)} disabled={!item.isActive} sx={{ mr: 1 }} >
                                                Close Ticket
                                            </Button>

                                            <Button variant="contained" color="success" size="small" onClick={() => navigate("/ticket-details?id=" + item._id)} >
                                                View Ticket
                                            </Button>
                                        </TableCell>

                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    {/* Pagination */}
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Pagination count={totalPages} page={page} onChange={handlePageChange} shape="rounded"
                            sx={{
                                '& .MuiPaginationItem-root': {
                                    '&.Mui-selected': {
                                        bgcolor: '#4B49AC',
                                        color: 'white',
                                    },
                                },
                            }}
                        />
                        <Select value={rowsPerPage} onChange={handleRowsPerPageChange} size="small"
                            sx={{ ml: 2, height: 35, bgcolor: '#f7f7f7', border: '1px solid #e2e2e2', '& .MuiSelect-select': { py: 0.75 }, }}
                            IconComponent={KeyboardArrowDownIcon} >
                            <MenuItem value={10}>10 / page</MenuItem>
                            <MenuItem value={20}>20 / page</MenuItem>
                            <MenuItem value={50}>50 / page</MenuItem>
                        </Select>
                    </Box>

                    {/* Add Support Ticket Modal */}
                    <Modal show={addSupportTicketModal} onHide={() => setAddSupportTicketModal(false)} style={{ zIndex: 9999 }} backdrop="static" keyboard={false} >
                        <Modal.Header closeButton>
                            <Modal.Title>Add Support Ticket</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form onSubmit={addSupportTicket}>
                                <div className="mb-3">

                                    <label htmlFor="title" className="col-form-label">Topic:</label>
                                    <input type="text" className="form-control" id="title" placeholder="Title" name='title' value={formData.title} onChange={handleInputChange} required />
                                    <label htmlFor="message" className="col-form-label">Description:</label>
                                    <textarea type="text" className="form-control" id="message" placeholder="Description" name='message' value={formData.message} onChange={handleInputChange} required />

                                    <label htmlFor="page_url" className="col-form-label">Page url:</label>
                                    <input type="text" className="form-control" id="page_url" placeholder="page_url" name='page_url' value={formData.page_url} onChange={handleInputChange} required />

                                    <label htmlFor="ticket_image" className="col-form-label">Ticket Image:</label>
                                    <input type="file" className="form-control" id="ticket_image" name='ticket_image' onChange={handleFileChange} />
                                </div>
                                <Modal.Footer>
                                    <Button variant="contained" type="submit" color="primary" sx={{ bgcolor: '#3A3897', '&:hover': { bgcolor: '#2A2878', } }}>Save</Button>
                                </Modal.Footer>
                            </form>
                        </Modal.Body>
                    </Modal>

                </Box>
            }
        </Layout>
    );
};

export default SupportTicket;